import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Map, MapMarker, Polyline, CustomOverlayMap } from 'react-kakao-maps-sdk';
import { Phone, Check, AlertTriangle, Eye, ArrowLeft } from 'lucide-react'; // 🚨 Chevron 아이콘 제거
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE_URL } from './config';

const KAKAO_APP_KEY = 'e8757f3638207e014bcea23f202b11d8'; 

const MARKER_IMGS = {
    start: {
        src: "https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/marker_red.png", 
        size: { width: 31, height: 35 }, 
        options: { offset: { x: 15, y: 35 } } 
    },
    end: {
        src: "https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/blue_b.png", 
        size: { width: 35, height: 40 }, 
        options: { offset: { x: 17, y: 40 } }
    }
};

export default function NavigationScreen({ userUid: propUserUid }) {
    const location = useLocation();
    const navigate = useNavigate();
    
    const userUid = propUserUid || localStorage.getItem('userUid');
    const { path, routeInfo } = location.state || {};

    const [map, setMap] = useState(null);
    const [currentPos, setCurrentPos] = useState(path ? path[0] : null); 
    const [passedPath, setPassedPath] = useState([]);
    const [remainPath, setRemainPath] = useState(path || []);
    const [remainingTimeStr, setRemainingTimeStr] = useState(routeInfo?.time || "계산중");
    const [arrivalTimeStr, setArrivalTimeStr] = useState("");
    const [isSOSPressed, setIsSOSPressed] = useState(false);
    const sosTimerRef = useRef(null);
    const [contacts, setContacts] = useState([]);
    const watchId = useRef(null);

    const [isSheetOpen, setIsSheetOpen] = useState(true);

    const SHEET_HEIGHT = 460; 
    const CLOSED_POS = 100;   
    const OPEN_POS = SHEET_HEIGHT + 15; 

    //  긴급 연락처 로드
    useEffect(() => {
        const fetchContacts = async () => {
            if (!userUid) return;
            try {
                const res = await axios.get(`${API_BASE_URL}/api/contacts/${userUid}`);
                setContacts(res.data);
            } catch (e) { console.error(e); }
        };
        fetchContacts();
    }, [userUid]);

    // 거리 계산 및 위치 추적
    useEffect(() => {
        if (!path || path.length < 2 || !navigator.geolocation) return;

        const getDistance = (lat1, lng1, lat2, lng2) => {
            if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
            const R = 6371e3;
            const φ1 = lat1 * Math.PI / 180, φ2 = lat2 * Math.PI / 180;
            const Δφ = (lat2 - lat1) * Math.PI / 180, Δλ = (lng2 - lng1) * Math.PI / 180;
            const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        };

        const minutes = parseInt(routeInfo?.time?.replace(/[^0-9]/g, '')) || 0;
        const now = new Date();
        now.setMinutes(now.getMinutes() + minutes);
        setArrivalTimeStr(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

        watchId.current = navigator.geolocation.watchPosition(
            (position) => {
                const newLat = position.coords.latitude;
                const newLng = position.coords.longitude;
                const newPos = { lat: newLat, lng: newLng };
                setCurrentPos(newPos);
                
                let minIdx = 0;
                let minDist = Infinity;
                path.forEach((p, i) => {
                    const d = getDistance(newLat, newLng, p.lat, p.lng);
                    if (d < minDist) { minDist = d; minIdx = i; }
                });

                setPassedPath(path.slice(0, minIdx + 1));
                setRemainPath(path.slice(minIdx));

                const remainingRatio = Math.max(0, (path.length - minIdx) / path.length);
                const leftMin = Math.ceil(minutes * remainingRatio);
                const newRemainingTimeStr = leftMin > 0 ? `${leftMin}분` : "곧 도착";
                
                setRemainingTimeStr(newRemainingTimeStr);

                const endPos = path[path.length - 1];
                if (getDistance(newLat, newLng, endPos.lat, endPos.lng) < 30) {
                    toast.success("도착 완료!");
                    navigator.geolocation.clearWatch(watchId.current);
                }
            },
            (err) => console.warn(err),
            { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
        );

        return () => {
            if (watchId.current) navigator.geolocation.clearWatch(watchId.current);
        };
    }, [path, map, routeInfo, navigate]);

    const startSOS = () => {
        setIsSOSPressed(true);
        sosTimerRef.current = setTimeout(() => {
            triggerSOSAction();
            setIsSOSPressed(false);
        }, 2000);
    };

    const endSOS = () => {
        if (sosTimerRef.current) {
            clearTimeout(sosTimerRef.current);
            setIsSOSPressed(false);
        }
    };

    const triggerSOSAction = () => {
        if (contacts.length === 0) {
            toast.error("연락처 없음. 112 연결.");
            window.location.href = 'tel:112';
            return;
        }
        const phoneNumbers = contacts.map(c => c.phone).join(',');
        const message = `[SafeWay SOS] 위치: https://map.kakao.com/link/map/${currentPos?.lat},${currentPos?.lng}`;
        const separator = navigator.userAgent.match(/iPhone|iPad/i) ? '&' : '?';
        window.location.href = `sms:${phoneNumbers}${separator}body=${encodeURIComponent(message)}`;
        toast.success(`보호자 ${contacts.length}명에게 연결`);
    };

    if (!path) return <div className="flex justify-center items-center h-screen">로딩중...</div>;

    return (
        <div className="fixed inset-0 bg-gray-100 font-sans overflow-hidden">
            
            {/* 1. 지도 */}
            <div className="absolute inset-0 z-0">
                <Map center={currentPos || path[0]} style={{ width: "100%", height: "100%" }} level={3} appkey={KAKAO_APP_KEY} onCreate={setMap}>
                    <MapMarker position={path[0]} image={MARKER_IMGS.start} />
                    <MapMarker position={path[path.length-1]} image={MARKER_IMGS.end} />
                    {currentPos && (
                        <CustomOverlayMap position={currentPos} zIndex={99}>
                            <div className="relative flex items-center justify-center">
                                <div className="w-5 h-5 bg-blue-600 rounded-full border-2 border-white shadow-lg z-20"></div>
                                <div className="absolute w-16 h-16 bg-blue-500 rounded-full opacity-30 animate-ping z-10"></div>
                            </div>
                        </CustomOverlayMap>
                    )}
                    <Polyline path={[passedPath]} strokeWeight={9} strokeColor={"#cbd5e1"} strokeOpacity={0.8} />
                    <Polyline path={[remainPath]} strokeWeight={9} strokeColor={"#2563eb"} strokeOpacity={1} />
                </Map>
            </div>

            {/* 상단 뒤로가기 */}
            <div className="absolute top-4 left-4 z-20">
                <button onClick={() => navigate(-1)} className="bg-white p-3 rounded-full shadow-md text-gray-700 active:scale-95">
                    <ArrowLeft className="w-6 h-6" />
                </button>
            </div>

            {/* 시간 정보 카드 */}
            <div 
                className="fixed left-4 right-4 z-50 transition-all duration-300 ease-in-out"
                style={{ 
                    bottom: isSheetOpen ? `${OPEN_POS}px` : `${CLOSED_POS}px` 
                }}
            >
                <div 
                    onClick={() => setIsSheetOpen(!isSheetOpen)}
                    className="bg-white rounded-3xl shadow-xl p-4 flex items-center justify-between border border-gray-100 cursor-pointer active:scale-[0.99] transition-transform"
                >
                    <div>
                        <p className="text-xs font-bold text-gray-400 mb-1">남은 시간</p>
                        <p className="text-3xl font-black text-blue-600 tracking-tighter">
                            {remainingTimeStr?.replace(/[^0-9]/g, '') || '0'}
                            <span className="text-lg ml-1 text-blue-500 font-bold">분</span>
                        </p>
                    </div>
                    
                    <div className="h-10 w-[1px] bg-gray-200 mx-4"></div>

                    <div className="text-right">
                        <p className="text-xs font-bold text-gray-400 mb-1">도착 예정</p>
                        <p className="text-xl font-bold text-gray-800 tracking-tight">
                            {arrivalTimeStr || '--:--'}
                        </p>
                    </div>
                </div>
            </div>

            {/* 하단 SOS 시트 */}
            <div 
                className={`fixed left-0 right-0 bottom-0 z-40 bg-white rounded-t-[2.5rem] shadow-[0_-5px_30px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-in-out
                ${isSheetOpen ? 'translate-y-0' : 'translate-y-[120%]'}`} 
                style={{ height: `${SHEET_HEIGHT}px` }}
            >
                {/* 핸들 */}
                <div 
                    onClick={() => setIsSheetOpen(false)}
                    className="w-full h-[30px] flex items-center justify-center cursor-pointer active:bg-gray-50 rounded-t-[2.5rem] absolute top-0 left-0 right-0 z-10"
                >
                    <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
                </div>

                {/* 내용물 컨테이너 */}
                <div className="px-6 pt-10 pb-14 flex flex-col h-full items-center justify-start">
                    
                    {/* 보호자 모니터링 */}
                    <div className="w-full bg-blue-50/80 px-4 py-3 rounded-xl flex items-center justify-between border border-blue-100 flex-shrink-0 mb-6">
                        <div className="flex items-center text-xs font-bold text-gray-700">
                            <Eye className="w-3 h-3 mr-2 text-green-500 animate-pulse" /> 
                            안심 귀가 모니터링 중
                        </div>
                        <div className="flex -space-x-2">
                            {contacts.length > 0 ? (
                                contacts.slice(0, 3).map((c, i) => (
                                    <div key={i} className="w-6 h-6 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-blue-700 shadow-sm">
                                        {c.name[0]}
                                    </div>
                                ))
                            ) : (
                                <span className="text-[10px] text-gray-400">보호자 없음</span>
                            )}
                        </div>
                    </div>

                    {/* SOS 버튼 */}
                    <div className="flex flex-col items-center justify-center">
                        <button
                            onMouseDown={startSOS} 
                            onMouseUp={endSOS} 
                            onMouseLeave={endSOS}
                            onTouchStart={startSOS} 
                            onTouchEnd={endSOS}
                            className={`w-32 h-32 rounded-full flex flex-col items-center justify-center text-white shadow-xl transition-all duration-200 
                                ${isSOSPressed 
                                    ? 'bg-red-700 scale-95 ring-8 ring-red-200' 
                                    : 'bg-red-500 hover:bg-red-600 ring-4 ring-red-100 animate-pulse'}`}
                        >
                            <AlertTriangle className="w-12 h-12 mb-1" />
                            <span className="text-2xl font-black tracking-widest">SOS</span>
                        </button>
                        
                        {isSOSPressed && (
                            <div className="absolute top-0 right-6 bg-gray-800 text-white text-xs px-2 py-1 rounded animate-bounce">
                                전송 중...
                            </div>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1">위급 시 2초간 꾹</p>
                    </div>

                    {/* 하단 버튼 */}
                    <div className="w-full grid grid-cols-2 gap-3 mt-8">
                        <a href="tel:112" className="flex items-center justify-center bg-gray-50 border border-gray-200 text-gray-600 py-3.5 rounded-xl font-bold shadow-sm active:scale-95 transition-transform text-sm">
                            <Phone className="w-4 h-4 mr-2 text-gray-500" /> 112 신고
                        </a>
                        <button 
                            onClick={() => { 
                                if(watchId.current) navigator.geolocation.clearWatch(watchId.current);
                                toast.success("안전하게 도착했습니다!"); 
                                navigate('/'); 
                            }}
                            className="flex items-center justify-center bg-green-500 text-white py-3.5 rounded-xl font-bold shadow-md shadow-green-200 active:scale-95 transition-transform text-sm"
                        >
                            <Check className="w-4 h-4 mr-2" /> 도착 완료
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}